import { chmod, mkdir, readFile, rename, rm, writeFile } from "node:fs/promises";
import { join } from "node:path";
import {
  encryptBackup,
  hashBackupCode,
  bytesToBase64,
  bytesToHex,
  type BackupBundle,
} from "@agentvault/crypto";
import type { PersistedState } from "./types.js";

const STATE_FILE = "agentvault.json";
const LEGACY_STATE_FILE = "secure-channel.json";

// State files contain deviceJwt and ratchet keys — restrict to owner only
const DIR_MODE = 0o700;
const FILE_MODE = 0o600;

export async function saveState(
  dataDir: string,
  state: PersistedState,
): Promise<void> {
  await mkdir(dataDir, { recursive: true, mode: DIR_MODE });
  const filePath = join(dataDir, STATE_FILE);
  await writeFile(filePath, JSON.stringify(state, null, 2), { encoding: "utf-8", mode: FILE_MODE });

  // Clean up legacy file after migration
  try {
    await rm(join(dataDir, LEGACY_STATE_FILE));
  } catch {
    // Already removed or never existed
  }
}

/**
 * Load raw persisted state from disk.
 * Returns the raw parsed JSON — caller is responsible for migration
 * from legacy format to current PersistedState.
 *
 * Checks agentvault.json first, falls back to secure-channel.json
 * for backward compatibility. Auto-migrates on first legacy load.
 */
export async function loadState(
  dataDir: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any | null> {
  // Try new filename first
  const filePath = join(dataDir, STATE_FILE);
  try {
    const raw = await readFile(filePath, "utf-8");
    return JSON.parse(raw);
  } catch {
    // Fall through to legacy
  }

  // Fall back to legacy filename
  const legacyPath = join(dataDir, LEGACY_STATE_FILE);
  try {
    const raw = await readFile(legacyPath, "utf-8");
    const parsed = JSON.parse(raw);
    // Auto-migrate: write to new filename, remove legacy
    await mkdir(dataDir, { recursive: true, mode: DIR_MODE });
    await writeFile(filePath, JSON.stringify(parsed, null, 2), { encoding: "utf-8", mode: FILE_MODE });
    await rm(legacyPath);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Check whether persisted state is valid and usable (has deviceId, deviceJwt, and at least one session).
 */
export async function isStateValid(dataDir: string): Promise<boolean> {
  try {
    const filePath = join(dataDir, STATE_FILE);
    const raw = await readFile(filePath, "utf-8");
    const parsed = JSON.parse(raw);
    return !!(
      parsed &&
      parsed.deviceId &&
      parsed.deviceJwt &&
      parsed.sessions &&
      Object.keys(parsed.sessions).length > 0
    );
  } catch {
    return false;
  }
}

/**
 * Create a backup of the state file (atomic write via tmp + rename).
 * Silent no-op if no state file exists.
 */
export async function backupState(dataDir: string): Promise<void> {
  const src = join(dataDir, STATE_FILE);
  const bak = join(dataDir, `${STATE_FILE}.bak`);
  const tmp = join(dataDir, `${STATE_FILE}.bak.tmp`);
  try {
    const data = await readFile(src, "utf-8");
    await writeFile(tmp, data, { encoding: "utf-8", mode: FILE_MODE });
    await rename(tmp, bak);
  } catch {
    // No state file or write failed — silent no-op
  }
}

/**
 * Restore state from backup if primary state is missing/empty/invalid but .bak exists and is valid.
 * Returns true if state was successfully restored.
 */
export async function restoreState(dataDir: string): Promise<boolean> {
  // Only restore if primary is missing or invalid
  const primaryValid = await isStateValid(dataDir);
  if (primaryValid) return false;

  const bak = join(dataDir, `${STATE_FILE}.bak`);
  try {
    const data = await readFile(bak, "utf-8");
    const parsed = JSON.parse(data);
    if (
      !parsed ||
      !parsed.deviceId ||
      !parsed.deviceJwt ||
      !parsed.sessions ||
      Object.keys(parsed.sessions).length === 0
    ) {
      return false;
    }
    await mkdir(dataDir, { recursive: true, mode: DIR_MODE });
    await writeFile(join(dataDir, STATE_FILE), data, { encoding: "utf-8", mode: FILE_MODE });
    return true;
  } catch {
    return false;
  }
}

/**
 * Upload encrypted backup to server. Converts PersistedState → BackupBundle,
 * encrypts with backup code, and PUTs to the backend.
 */
export async function uploadBackupToServer(
  state: PersistedState,
  backupCode: string,
  apiUrl: string,
  deviceJwt: string,
): Promise<void> {
  // Build BackupBundle from PersistedState
  const ratchets: Record<string, string> = {};
  for (const [convId, session] of Object.entries(state.sessions)) {
    if (session.ratchetState) {
      ratchets[convId] = session.ratchetState;
    }
  }

  const bundle: BackupBundle = {
    version: 1,
    createdAt: new Date().toISOString(),
    deviceId: state.deviceId,
    identityKeypair: state.identityKeypair,
    ephemeralKeypair: state.ephemeralKeypair,
    fingerprint: state.fingerprint,
    ratchets,
    sessions: Object.fromEntries(
      Object.entries(state.sessions).map(([convId, s]) => [
        convId,
        {
          ownerDeviceId: s.ownerDeviceId,
          ratchetState: s.ratchetState,
          activated: s.activated,
        },
      ]),
    ),
    topics: state.topics,
    defaultTopicId: state.defaultTopicId,
    groupId: state.groupId,
    hubAddress: state.hubAddress,
  };

  const encrypted = await encryptBackup(bundle, backupCode);
  const codeHash = await hashBackupCode(backupCode);

  const resp = await fetch(`${apiUrl}/api/v1/backup`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${deviceJwt}`,
    },
    body: JSON.stringify({
      backup_blob: bytesToBase64(encrypted),
      backup_code_hash: bytesToHex(codeHash),
    }),
  });

  if (!resp.ok) {
    throw new Error(`Backup upload failed: ${resp.status}`);
  }
}

export async function clearState(dataDir: string): Promise<void> {
  // Remove both files to cover migration edge cases
  for (const filename of [STATE_FILE, LEGACY_STATE_FILE]) {
    try {
      await rm(join(dataDir, filename));
    } catch {
      // File doesn't exist — nothing to clear
    }
  }
}
